import { apm } from '@elastic/apm-rum';
import {
    convertDateFromSourceToUserTimeZone,
    parseDate,
    parseISOString,
} from '@/core/lib/date';
import type { CustomError } from '@/core/lib/errorType';
import { isMainMarket } from '@/core/lib/oddsHelper';
import { formatJersey } from '@/core/oddsApi/helpers/formatStreamEvents';
import { formatTranslation } from '@/core/oddsApi/helpers/formatTranslation';
import type {
    EventResultTypeFragment,
    EventTypeEuroFragment,
    EventTypeFragment,
    MpRecommendationEventTypeFragment,
    TournamentEventTypeFragment,
} from '@/core/oddsApi/oddsApiType';
import {
    EnumHandicapType,
    EnumSportType,
} from '@/core/oddsApi/oddsApiType';
import type {
    IEvent,
    IEventEuro,
    IEventExtraInfo,
    IMpRecommendationEvent,
} from '@/interface/IEvent';
import type { ISimpleEventResult } from '@/interface/ISimpleEventResult';
import type {
    BetRadarStreamStatus,
    BetTypeGroup,
    MarketType,
} from '@/interface/enum';
import {
    LeagueSize,
    ShowTimeDisplayType,
    SportType,
    TimezoneOffset,
} from '@/interface/enum';
import type { ITournamentEvent } from '@/interface/tournamentCenter';

function formatExtraInfo(event: EventTypeFragment | TournamentEventTypeFragment): IEventExtraInfo {
    return event.mainMarketEventResult.extraInfo
        ? {
            injuryTime: event.mainMarketEventResult.extraInfo.injuryTime,
            period: event.mainMarketEventResult.extraInfo.period,
            round: event.mainMarketEventResult.extraInfo.round,
            statisticsUrl:
          event.mainMarketEventResult.extraInfo.statisticsUrl ?? '',
            periodStartTime: event.mainMarketEventResult.extraInfo.periodStartTime,
        }
        : {
            injuryTime: 0,
            period: 0,
            round: 0,
            statisticsUrl: null,
            periodStartTime: null,
        };
}

type EventWithHandicap = {
    isLive: boolean;
    mainMarketEventResult: {
        liveHandicapType: EnumHandicapType;
        nonLiveHandicapType: EnumHandicapType;
    };
};

function getHandicapType(event: EventWithHandicap) {
    return event.isLive
        ? event.mainMarketEventResult.liveHandicapType
        : event.mainMarketEventResult.nonLiveHandicapType;
}

export function formatEvent(
    event: EventTypeFragment,
    rawEventResults: EventResultTypeFragment[],
): IEvent | null {
    try {
        return toEvent(event, rawEventResults);
    } catch (error) {
        captureFormatEventError(error, event, rawEventResults);
        return null;
    }
}

// `stack` is copied so APM still shows which `.map` failed.
// Elastic RUM ships only primitive own properties (as context.custom), so the inputs go in as strings.
function captureFormatEventError(error: unknown, event: EventTypeFragment, rawEventResults: EventResultTypeFragment[]) {
    const original = error instanceof Error ? error : new Error(String(error));
    const apmError: CustomError = {
        name: original.name,
        message: `[formatEvent] eventId=${event.id} ${original.message}`,
        stack: original.stack ?? '',
        rawEvent: stringifyRawInput(event),
        rawEventResults: stringifyRawInput(rawEventResults),
    };
    apm.captureError(apmError);
}

function stringifyRawInput(value: unknown): string {
    try {
        return JSON.stringify(value);
    } catch (error) {
        return `[unserializable: ${String(error)}]`;
    }
}

function toEvent(
    event: EventTypeFragment,
    rawEventResults: EventResultTypeFragment[],
): IEvent {
    const handicapType = getHandicapType(event);

    const eventResults = rawEventResults.map<ISimpleEventResult>(
        eventResult => ({
            id: eventResult.id,
            homeTeam: {
                liveScore: eventResult.liveHomeScore,
                redCard: eventResult.extraInfo?.homeRedCards ?? null,
            },
            awayTeam: {
                liveScore: eventResult.liveAwayScore,
                redCard: eventResult.extraInfo?.awayRedCards ?? null,
            },
            marketGroup: {
                id: eventResult.marketGroup?.id ?? -1,
                marketGroupName:
          eventResult.marketGroup?.marketGroupName.map(x => formatTranslation(x)) ?? [],
                displayPriority: eventResult.marketGroup?.displayPriority ?? 0,
            },
        }),
    );

    const mainMarketEventResult = eventResults.find(eventResult => isMainMarket(eventResult.marketGroup.id));

    return {
        id: event.id,
        eventCode: event.eventCode,
        eventDate: parseISOString(event.eventDate),
        hasStatistics: event.hasStatistics,
        liveCourtMatchId: event.liveCourtMatchId,
        showTime: parseDate(
            event.showTime,
            'MM/dd/yyyy HH:mm',
            TimezoneOffset.Singapore,
        ),
        showTimeType: event.showTimeType,
        marketTypeCount: getMarketTypeCount(event.marketTypeCount),
        dynamicMarketCount: event.dynamicMarketCount ?? 0,
        isLive: event.isLive,
        isSimulation: event.isSimulation,
        isTvLive: ShowTimeDisplayType.isTvLive(event.showTimeType),
        isSboLive: ShowTimeDisplayType.isSboLive(event.showTimeType),
        tvChannels: event.tvChannels,
        absKickOffTime: parseISOString(event.absKickoffTime),
        eventResults,
        handicapType,
        isMixParlay: event.mainMarketEventResult.isMixParlay,
        extraInfo: formatExtraInfo(event),
        awayTeam: {
            name: event.awayTeam?.teamName.map(x => formatTranslation(x)) ?? [],
            redCard: mainMarketEventResult?.awayTeam.redCard ?? null,
            liveScore: mainMarketEventResult?.awayTeam.liveScore ?? null,
            isFavorite: handicapType === EnumHandicapType.Away,
        },
        homeTeam: {
            name: event.homeTeam?.teamName.map(x => formatTranslation(x)) ?? [],
            redCard: mainMarketEventResult?.homeTeam.redCard ?? null,
            liveScore: mainMarketEventResult?.homeTeam.liveScore ?? null,
            isFavorite: handicapType === EnumHandicapType.Home,
        },
        league: {
            id: event.tournament.id,
            name: event.tournament.tournamentName.map(x => formatTranslation(x)),
            info: event.tournament.tournamentInfo.map(x => formatTranslation(x)),
            size: LeagueSize.fromOddsApi(event.tournament.tournamentSize),
        },
        sportType: SportType.fromOddsApi(
            event.tournament.sport?.sportType ?? EnumSportType.Soccer,
        ),
        stream: formatStream(event),
        outrightIds: event.isLive ? [] : event.nonLiveOutrightIds,
        realKickoffTime: convertDateFromSourceToUserTimeZone(
            parseISOString(event.realKickoffTime),
            TimezoneOffset.Server,
        ),
    };
}

function getMarketTypeCount(
    marketTypeCount: string[],
): Partial<Record<BetTypeGroup, Partial<Record<MarketType, number>>>> {
    const result: Partial<
    Record<BetTypeGroup, Partial<Record<MarketType, number>>>
    > = {};
    marketTypeCount.forEach((marketTypeStr) => {
        const [marketGroup, marketType, count] = marketTypeStr
            .split(':')
            .map(x => parseInt(x)) as [BetTypeGroup, MarketType, number];
        result[marketGroup] ??= {};
        result[marketGroup][marketType] = count;
    });
    return result;
}

type EventWithStream = Pick<EventTypeFragment, 'id' | 'stream'>;

export function formatStream(event: EventWithStream) {
    return event.stream
        ? {
            videoUrl: event.stream.videoUrl ?? null,
            betRadar: event.stream.betRadar
                ? {
                    eventId: event.id,
                    streamId: event.stream.betRadar.streamId,
                    streamStatus: event.stream.betRadar
                        .streamStatus as BetRadarStreamStatus,
                    allowedCountries: event.stream.betRadar.allowedCountries,
                }
                : null,
            gLive: event.stream.gLive
                ? {
                    eventId: event.id,
                    isPlaying: event.stream.gLive.isPlaying,
                }
                : null,
            betConstruct: event.stream.betConstruct
                ? {
                    eventId: event.stream.betConstruct.eventId,
                    allowedCountries: event.stream.betConstruct.allowedCountries,
                    isPlaying: event.stream.betConstruct.isPlaying,
                }
                : null,
            betGenius: event.stream.betGenius
                ? {
                    eventId: event.stream.betGenius.eventId,
                    allowedCountries: event.stream.betGenius.allowedCountries,
                    isPlaying: event.stream.betGenius.isPlaying,
                    providerEventId: event.stream.betGenius.providerEventId,
                }
                : null,
        }
        : null;
}

export function formatEventEuro(event: EventTypeEuroFragment): IEventEuro {
    return {
        id: event.id,
        eventCode: event.eventCode,
        hasStatistics: event.hasStatistics,
        liveCourtMatchId: event.liveCourtMatchId,
        showTime: parseDate(
            event.showTime,
            'MM/dd/yyyy HH:mm',
            TimezoneOffset.Singapore,
        ),
        showTimeType: event.showTimeType,
        marketTypeCount: getMarketTypeCount(event.marketTypeCount),
        isLive: event.isLive,
        isSimulation: event.isSimulation,
        absKickOffTime: parseISOString(event.absKickoffTime),
        extraInfo: event.mainMarketEventResult.extraInfo
            ? {
                injuryTime: event.mainMarketEventResult.extraInfo.injuryTime,
                period: event.mainMarketEventResult.extraInfo.period,
                periodStartTime:
            event.mainMarketEventResult.extraInfo.periodStartTime,
            }
            : null,
        awayTeam: {
            name: event.awayTeam?.teamName.map(x => formatTranslation(x)) ?? [],
            jersey: formatJersey(event.awayJersey),
            redCard: event.mainMarketEventResult.extraInfo?.awayRedCards ?? null,
            liveScore: event.mainMarketEventResult.liveAwayScore,
        },
        homeTeam: {
            name: event.homeTeam?.teamName.map(x => formatTranslation(x)) ?? [],
            jersey: formatJersey(event.homeJersey),
            redCard: event.mainMarketEventResult.extraInfo?.homeRedCards ?? null,
            liveScore: event.mainMarketEventResult.liveHomeScore,
        },
        league: {
            id: event.tournament.id,
            name: event.tournament.tournamentName.map(x => formatTranslation(x)),
            info: event.tournament.tournamentInfo.map(x => formatTranslation(x)),
            size: LeagueSize.fromOddsApi(event.tournament.tournamentSize),
            region: {
                id: event.tournament.region?.id ?? 0,
                name: event.tournament.region?.regionName.map(x => formatTranslation(x)) ?? [],
            },
        },
        sportType: SportType.fromOddsApi(
            event.tournament.sport?.sportType ?? EnumSportType.Soccer,
        ),
        stream: formatStream(event),
        realKickoffTime: convertDateFromSourceToUserTimeZone(
            parseISOString(event.realKickoffTime),
            TimezoneOffset.Server,
        ),
    };
}

export function formatMpRecommendationEvent(
    event: MpRecommendationEventTypeFragment,
): IMpRecommendationEvent {
    return {
        id: event.id,
        isSimulation: event.isSimulation,
        mainMarketEventResult: {
            id: event.mainMarketEventResult.id,
            homeTeam: {
                liveScore: event.mainMarketEventResult.liveHomeScore,
            },
            awayTeam: {
                liveScore: event.mainMarketEventResult.liveAwayScore,
            },
            marketGroup: {
                id: event.mainMarketEventResult.marketGroup?.id ?? -1,
                marketGroupName:
          event.mainMarketEventResult.marketGroup?.marketGroupName.map(x => formatTranslation(x)) ?? [],
                displayPriority:
          event.mainMarketEventResult.marketGroup?.displayPriority ?? 0,
            },
        },
        homeTeam: {
            teamName: event.homeTeam?.teamName.map(x => formatTranslation(x)) ?? [],
        },
        awayTeam: {
            teamName: event.awayTeam?.teamName.map(x => formatTranslation(x)) ?? [],
        },
        league: {
            id: event.tournament.id,
            name: event.tournament.tournamentName.map(x => formatTranslation(x)),
            size: LeagueSize.fromOddsApi(event.tournament.tournamentSize),
        },
        sportType: SportType.fromOddsApi(
            event.tournament.sport?.sportType ?? EnumSportType.Soccer,
        ),
        isLive: event.isLive,
    };
}

export function formatTournamentEvent(event: TournamentEventTypeFragment, rawEventResults: EventResultTypeFragment[]): ITournamentEvent {
    const handicapType = getHandicapType(event);

    const eventResults = rawEventResults.map<ISimpleEventResult>(
        eventResult => ({
            id: eventResult.id,
            homeTeam: {
                liveScore: eventResult.liveHomeScore,
                redCard: eventResult.extraInfo?.homeRedCards ?? null,
            },
            awayTeam: {
                liveScore: eventResult.liveAwayScore,
                redCard: eventResult.extraInfo?.awayRedCards ?? null,
            },
            marketGroup: {
                id: eventResult.marketGroup?.id ?? -1,
                marketGroupName:
          eventResult.marketGroup?.marketGroupName.map(x => formatTranslation(x)) ?? [],
                displayPriority: eventResult.marketGroup?.displayPriority ?? 0,
            },
        }),
    );

    const mainMarketEventResult = eventResults.find(eventResult => isMainMarket(eventResult.marketGroup.id));

    return {
        id: event.id,
        eventCode: event.eventCode,
        eventDate: parseISOString(event.eventDate),
        hasStatistics: event.hasStatistics,
        liveCourtMatchId: event.liveCourtMatchId,
        showTime: parseDate(
            event.showTime,
            'MM/dd/yyyy HH:mm',
            TimezoneOffset.Singapore,
        ),
        showTimeType: event.showTimeType,
        marketTypeCount: getMarketTypeCount(event.marketTypeCount),
        isLive: event.isLive,
        isSimulation: event.isSimulation,
        isSboLive: ShowTimeDisplayType.isSboLive(event.showTimeType),
        absKickOffTime: parseISOString(event.absKickoffTime),
        eventResults,
        extraInfo: formatExtraInfo(event),
        awayTeam: {
            name: event.awayTeam?.teamName.map(x => formatTranslation(x)) ?? [],
            countryName: event.awayTeam?.countryName ?? null,
            redCard: mainMarketEventResult?.awayTeam.redCard ?? null,
            liveScore: mainMarketEventResult?.awayTeam.liveScore ?? null,
            isFavorite: handicapType === EnumHandicapType.Away,
        },
        homeTeam: {
            name: event.homeTeam?.teamName.map(x => formatTranslation(x)) ?? [],
            countryName: event.homeTeam?.countryName ?? null,
            redCard: mainMarketEventResult?.homeTeam.redCard ?? null,
            liveScore: mainMarketEventResult?.homeTeam.liveScore ?? null,
            isFavorite: handicapType === EnumHandicapType.Home,
        },
        league: {
            id: event.tournament.id,
            name: event.tournament.tournamentName.map(x => formatTranslation(x)),
            info: event.tournament.tournamentInfo.map(x => formatTranslation(x)),
            size: LeagueSize.fromOddsApi(event.tournament.tournamentSize),
        },
        sportType: SportType.fromOddsApi(
            event.tournament.sport?.sportType ?? EnumSportType.Soccer,
        ),
        stream: formatStream(event),
        realKickoffTime: convertDateFromSourceToUserTimeZone(
            parseISOString(event.realKickoffTime),
            TimezoneOffset.Server,
        ),
    };
}
